import React, { useState, useEffect, useRef } from "react";
import UserSidebar from "../../components/UserSidebar";
import API from "../../services/api";
import {
  FiUser, FiMail, FiLock, FiCamera, FiSave,
  FiShield, FiEye, FiEyeOff,
  FiAlertTriangle, FiHelpCircle, FiInfo, FiExternalLink,
  FiCheckCircle
} from "react-icons/fi";

const MEDIA_BASE = "https://api.ajplservices.tech/uploads/profile/";

const UserSettings = () => {
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");

  // Profile Form
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  // Password Form
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const fileRef = useRef();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await API.get("/auth/profile");
      if (res.data?.success && res.data.data) {
        const u = res.data.data;
        setProfile(u);
        setFullName(u.full_name || "");
        setPhone(u.phone || "");
        if (u.profile_image) {
          setImagePreview(`${MEDIA_BASE}${u.profile_image}`);
        }
      }
    } catch (err) {
      console.error("Profile fetch error:", err);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const fd = new FormData();
      fd.append("full_name", fullName);
      fd.append("phone", phone);
      if (imageFile) fd.append("profile_image", imageFile);

      const res = await API.put("/auth/update-profile", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data?.success) {
        setProfileMsg({ type: "success", text: "Profile details updated successfully!" });
        if (res.data.data?.full_name) {
          localStorage.setItem("full_name", res.data.data.full_name);
        }
        setTimeout(() => setProfileMsg(null), 4000);
      }
    } catch (err) {
      setProfileMsg({ type: "error", text: err.response?.data?.message || "Failed to update profile" });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setPasswordMsg({ type: "error", text: "Password must be at least 6 characters long." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: "error", text: "New passwords do not match." });
      return;
    }

    setPasswordSaving(true);
    setPasswordMsg(null);
    try {
      const res = await API.post("/auth/change-password", { newPassword });
      if (res.data?.success) {
        setPasswordMsg({ type: "success", text: "Security password changed successfully!" });
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => setPasswordMsg(null), 4000);
      }
    } catch (err) {
      setPasswordMsg({ type: "error", text: err.response?.data?.message || "Password update failed." });
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#F8FAFC", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <UserSidebar />

      <div style={{ marginLeft: 280, flex: 1, minWidth: 0, padding: "32px 36px 100px", boxSizing: "border-box" }}>
        {/* ── HEADER ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#D4AF37", fontWeight: 800, letterSpacing: 0.8, marginBottom: 4 }}>
              <FiShield size={12} />
              <span>EMPLOYEE ACCOUNT PREFERENCES</span>
            </div>
            <h1 style={{ margin: "0 0 4px", fontSize: 24, fontWeight: 900, color: "#1B2559", letterSpacing: "-0.5px" }}>Profile & Settings</h1>
            <p style={{ margin: 0, fontSize: 13, color: "#A3AED0", fontWeight: 500 }}>Manage your personal details, credentials, and organizational contact preferences.</p>
          </div>
        </div>

        {/* ── TAB BAR ── */}
        <div style={{ display: "flex", gap: 8, borderBottom: "1.5px solid #F0F2FA", marginBottom: 28 }}>
          {[
            { id: "profile", label: "My Profile", icon: <FiUser size={15} /> },
            { id: "password", label: "Security & Password", icon: <FiLock size={15} /> },
            { id: "help", label: "Help & Support", icon: <FiHelpCircle size={15} /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "12px 24px", border: "none", background: "none",
                cursor: "pointer", fontSize: 13, fontWeight: 800,
                color: activeTab === tab.id ? "#D4AF37" : "#A3AED0",
                borderBottom: activeTab === tab.id ? "3px solid #D4AF37" : "3px solid transparent",
                fontFamily: "inherit", transition: "all 0.15s"
              }}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── TAB 1: PROFILE EDIT (FULL WIDTH STRETCH) ── */}
        {activeTab === "profile" && (
          <div style={{ width: "100%" }}>
            <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 32, border: "1.5px solid #F0F2FA", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              {profileMsg && (
                <div style={{
                  padding: "12px 18px", borderRadius: 12, marginBottom: 20, fontSize: 13, fontWeight: 700,
                  display: "flex", alignItems: "center", gap: 8,
                  background: profileMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                  color: profileMsg.type === "success" ? "#10B981" : "#EF4444",
                  border: `1px solid ${profileMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`
                }}>
                  {profileMsg.type === "success" ? <FiCheckCircle size={16} /> : <FiAlertTriangle size={16} />}
                  <span>{profileMsg.text}</span>
                </div>
              )}

              <form onSubmit={handleProfileSubmit}>
                {/* Avatar Picker */}
                <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid #F0F2FA" }}>
                  <div style={{ position: "relative" }}>
                    <div style={{
                      width: 76, height: 76, borderRadius: "50%",
                      background: "#FFFDF5", border: "2px solid #F5E8BA",
                      overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      {imagePreview ? (
                        <img src={imagePreview} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <FiUser size={34} color="#D4AF37" />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => fileRef.current.click()}
                      style={{
                        position: "absolute", bottom: -2, right: -2, width: 28, height: 28,
                        borderRadius: "50%", background: "#1B2559", color: "#D4AF37",
                        border: "2px solid #FFF", display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer"
                      }}
                      title="Upload Avatar"
                    >
                      <FiCamera size={13} />
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImagePick} />
                  </div>
                  <div>
                    <h3 style={{ margin: "0 0 3px", fontSize: 16, fontWeight: 800, color: "#1B2559" }}>{fullName || "User Profile"}</h3>
                    <p style={{ margin: "0 0 6px", fontSize: 12, color: "#A3AED0" }}>{profile?.department || "General Department"} · {profile?.designation || "Employee"}</p>
                    <span style={{ fontSize: 11, color: "#D4AF37", fontWeight: 700 }}>Click camera icon to change profile photo</span>
                  </div>
                </div>

                {/* Form Fields */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 24 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      required
                      value={fullName}
                      onChange={e => setFullName(e.target.value)}
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 12,
                        background: "#F8FAFC", border: "1.5px solid #F0F2FA",
                        color: "#1B2559", fontSize: 13, fontWeight: 600, outline: "none",
                        fontFamily: "inherit", boxSizing: "border-box"
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Email Address (Locked)
                    </label>
                    <input
                      type="email"
                      disabled
                      value={profile?.email || "user@achalsupport.com"}
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 12,
                        background: "#F1F5F9", border: "1.5px solid #F0F2FA",
                        color: "#94A3B8", fontSize: 13, fontWeight: 600, outline: "none",
                        fontFamily: "inherit", cursor: "not-allowed", boxSizing: "border-box"
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="+91..."
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 12,
                        background: "#F8FAFC", border: "1.5px solid #F0F2FA",
                        color: "#1B2559", fontSize: 13, fontWeight: 600, outline: "none",
                        fontFamily: "inherit", boxSizing: "border-box"
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      System Role
                    </label>
                    <input
                      type="text"
                      disabled
                      value={localStorage.getItem("role") || "USER"}
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 12,
                        background: "#F1F5F9", border: "1.5px solid #F0F2FA",
                        color: "#D4AF37", fontSize: 13, fontWeight: 800, outline: "none",
                        fontFamily: "inherit", cursor: "not-allowed", boxSizing: "border-box"
                      }}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <button
                    type="submit"
                    disabled={profileSaving}
                    style={{
                      padding: "12px 26px", borderRadius: 12, border: "none",
                      background: "#1B2559", color: "#D4AF37",
                      fontSize: 13, fontWeight: 800, cursor: profileSaving ? "not-allowed" : "pointer",
                      letterSpacing: 0.4, display: "inline-flex", alignItems: "center", gap: 8,
                      boxShadow: "0 6px 18px rgba(27,37,89,0.15)"
                    }}
                  >
                    <FiSave size={15} />
                    <span>{profileSaving ? "Saving..." : "Save Profile Details"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── TAB 2: CHANGE PASSWORD (FULL WIDTH STRETCH) ── */}
        {activeTab === "password" && (
          <div style={{ width: "100%" }}>
            <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 32, border: "1.5px solid #F0F2FA", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 800, color: "#1B2559" }}>Change Account Password</h3>
                <p style={{ margin: 0, fontSize: 12, color: "#A3AED0" }}>Ensure your account uses a secure password with at least 6 characters.</p>
              </div>

              {passwordMsg && (
                <div style={{
                  padding: "12px 18px", borderRadius: 12, marginBottom: 20, fontSize: 13, fontWeight: 700,
                  display: "flex", alignItems: "center", gap: 8,
                  background: passwordMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                  color: passwordMsg.type === "success" ? "#10B981" : "#EF4444",
                  border: `1px solid ${passwordMsg.type === "success" ? "#A7F3D0" : "#FECACA"}`
                }}>
                  {passwordMsg.type === "success" ? <FiCheckCircle size={16} /> : <FiAlertTriangle size={16} />}
                  <span>{passwordMsg.text}</span>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 24 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      New Password
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showNew ? "text" : "password"}
                        required
                        placeholder="Enter at least 6 characters..."
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        style={{
                          width: "100%", padding: "12px 42px 12px 14px", borderRadius: 12,
                          background: "#F8FAFC", border: "1.5px solid #F0F2FA",
                          color: "#1B2559", fontSize: 13, fontWeight: 600, outline: "none",
                          fontFamily: "inherit", boxSizing: "border-box"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew(!showNew)}
                        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#A3AED0", cursor: "pointer", display: "flex", padding: 0 }}
                      >
                        {showNew ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#A3AED0", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      Confirm New Password
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showConfirm ? "text" : "password"}
                        required
                        placeholder="Re-type new password..."
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        style={{
                          width: "100%", padding: "12px 42px 12px 14px", borderRadius: 12,
                          background: "#F8FAFC", border: "1.5px solid #F0F2FA",
                          color: "#1B2559", fontSize: 13, fontWeight: 600, outline: "none",
                          fontFamily: "inherit", boxSizing: "border-box"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#A3AED0", cursor: "pointer", display: "flex", padding: 0 }}
                      >
                        {showConfirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <button
                    type="submit"
                    disabled={passwordSaving}
                    style={{
                      padding: "12px 26px", borderRadius: 12, border: "none",
                      background: "#1B2559", color: "#D4AF37", fontSize: 13, fontWeight: 800,
                      cursor: passwordSaving ? "not-allowed" : "pointer", letterSpacing: 0.4,
                      display: "inline-flex", alignItems: "center", gap: 8,
                      boxShadow: "0 6px 18px rgba(27,37,89,0.15)"
                    }}
                  >
                    <FiLock size={15} />
                    <span>{passwordSaving ? "Updating Password..." : "Update Password"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── TAB 3: HELP & SUPPORT (FULL WIDTH STRETCH) ── */}
        {activeTab === "help" && (
          <div style={{ width: "100%" }}>
            <div style={{ background: "#FFFFFF", borderRadius: 20, padding: 32, border: "1.5px solid #F0F2FA", boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 800, color: "#1B2559" }}>Achal Support Help Desk</h3>
              <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748B", lineHeight: 1.6 }}>
                For operational escalations, urgent facility requests, or IT network access, contact internal support.
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <a
                  href="mailto:ajplgservices@gmail.com?subject=Employee%20Support%20Request"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "18px 22px", borderRadius: 14, background: "#F8FAFC",
                    border: "1px solid #F0F2FA", textDecoration: "none", color: "#1B2559"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: "#FFFDF5", border: "1px solid #F5E8BA", display: "flex", alignItems: "center", justifyContent: "center", color: "#D4AF37" }}>
                      <FiMail size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>Email Support</div>
                      <div style={{ fontSize: 12, color: "#A3AED0" }}>ajplgservices@gmail.com</div>
                    </div>
                  </div>
                  <FiExternalLink size={16} color="#D4AF37" />
                </a>

                <div style={{ padding: "18px 22px", borderRadius: 14, background: "#F8FAFC", border: "1px solid #F0F2FA", display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: "#FFFDF5", border: "1px solid #F5E8BA", display: "flex", alignItems: "center", justifyContent: "center", color: "#D4AF37" }}>
                    <FiInfo size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>Achal Support Hub</div>
                    <div style={{ fontSize: 12, color: "#A3AED0" }}>Version 1.0.1 (Internal Service Portal)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSettings;
